import { useState } from 'react';
import { useRouter } from 'next/router';
import { filterValuesSeparator } from 'shared-helpers/consts';
import { useReduxState } from 'hooks';
import { Expand, Link } from 'components/common';
import { RequireAllToggle } from 'components/common/filter';
import { Icon } from 'components/common/icons';

interface Props {
  activeFilters: string[];
  activeRequireAllKeys: string[];
  filter: string;
  filterKey: string;
  filters: { [groupHeader: string]: Record<'label' | 'value', string>[] };
  getBaseUrl: () => string;
  isRequireAllEnabled?: boolean;
  isSeparateFilter?: boolean;
  shallowNavigation: boolean;
}

const serializePresentInAllParam = (fields: string[]) =>
  fields.length ? `&presentInAll=${fields.join(filterValuesSeparator)}` : '';

const PageSideNavFiltersInstance = ({
  activeFilters = [],
  activeRequireAllKeys,
  filter,
  filterKey,
  filters,
  getBaseUrl,
  isRequireAllEnabled = false,
  isSeparateFilter,
  shallowNavigation
}: Props) => {
  const {
    serverConfig: { toggleProductFilterOptions },
    theme
  } = useReduxState();

  const router = useRouter();

  const [show, setShow] = useState(
    !toggleProductFilterOptions || isSeparateFilter
  );

  const isRequireAllActive = activeRequireAllKeys.includes(filterKey);
  const numberOfActives = (filters[filter] || []).filter((filt) =>
    activeFilters.includes(filt.value)
  ).length;

  const getPresentInAllParam = (isDeselectingLast: boolean) => {
    if (!isDeselectingLast || !isRequireAllActive) {
      return serializePresentInAllParam(activeRequireAllKeys);
    }

    return serializePresentInAllParam(
      activeRequireAllKeys.filter((field) => field !== filterKey)
    );
  };

  const handleRequireAllToggle = () => {
    const nextRequireAllKeys = isRequireAllActive
      ? activeRequireAllKeys.filter((key) => key !== filterKey)
      : [...activeRequireAllKeys, filterKey];

    const url =
      getBaseUrl() +
      activeFilters.join('') +
      serializePresentInAllParam(nextRequireAllKeys);

    router.push(url, url, shallowNavigation ? { shallow: true } : {});
  };

  return (
    <>
      {isSeparateFilter ? null : toggleProductFilterOptions ? (
        <div
          className="group-title group-title-toggle"
          onClick={() => setShow(!show)}
          tabIndex={0}
        >
          <div className="group-title-text">
            <div>{filter}</div>

            <div className="group-title-count">
              {` (${numberOfActives}/${filters[filter]?.length})`}
            </div>
          </div>

          {show ? (
            <Icon
              icon="angle-up"
              style={{
                marginLeft: 5,
                marginRight: 5,
                position: 'relative',
                top: 1
              }}
            />
          ) : (
            <Icon
              icon="angle-down"
              style={{
                marginLeft: 5,
                marginRight: 5,
                position: 'relative',
                top: 1
              }}
            />
          )}
        </div>
      ) : (
        <div className="group-title">{filter}</div>
      )}

      <Expand isExpanded={show}>
        <div className="filters-instance">
          {isRequireAllEnabled && filters[filter].length > 1 && (
            <RequireAllToggle
              handleToggle={handleRequireAllToggle}
              isActive={isRequireAllActive}
              style={{ padding: 5 }}
            />
          )}

          {filters[filter].map((filterOption, i) => {
            const isActive = activeFilters.includes(filterOption.value);
            const isDeselectingLast = isActive && numberOfActives === 1;

            let href =
              getBaseUrl() +
              activeFilters.join('') +
              getPresentInAllParam(isDeselectingLast);

            href = isActive
              ? href.replace(filterOption.value, '')
              : href + filterOption.value;

            return (
              <Link
                href={href}
                key={filterOption.label || i}
                style={{ textDecoration: 'none' }}
                {...(shallowNavigation ? { shallow: true } : {})}
              >
                <div className="link">
                  <div
                    className={`link-icon ${
                      isActive ? 'link-icon-active' : ''
                    }`}
                  >
                    <Icon fixedWidth={true} icon="check" />
                  </div>
                  {filterOption.label}
                </div>
              </Link>
            );
          })}
        </div>
      </Expand>

      <style jsx>{`
        .group-title {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          margin: 20px 7px 3px;
        }
        .group-title-toggle {
          align-items: center;
          cursor: pointer;
          display: flex;
          gap: 5px;
        }
        .group-title-text {
          align-items: center;
          display: flex;
          flex-grow: 1;
          gap: 5px;
        }
        .group-title-count {
          font-size: 1.3rem;
          font-weight: 400;
        }
        .link {
          background-color: white;
          border-radius: 8px;
          color: ${theme.textColor};
          display: flex;
          gap: 7px;
          margin-top: 5px;
          padding: 6px 8px;
          text-decoration: none;
          transition: background-color 0.3s;
        }
        .link:hover {
          background-color: ${theme.lightGrey};
        }
        .link-icon {
          background-color: ${theme.lightGreyDark};
          border-radius: 5px;
          color: transparent;
          flex-shrink: 0;
          font-size: 0.8rem;
          padding: 3px 0;
          position: relative;
          text-align: center;
          top: 1px;
          width: 16px;
          height: 16px;
        }
        .link-icon-active {
          background-color: ${theme.successColor};
          border-color: ${theme.successColor};
          color: ${theme.successTextColor};
        }
      `}</style>
    </>
  );
};

export default PageSideNavFiltersInstance;
